import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Ban, CheckCircle, Clock, Calendar, AlertTriangle, UserX, Shield } from 'lucide-react';

interface UserAccessManagerProps {
  userId: string;
  userName: string;
  userEmail: string;
  isActive: boolean;
  suspendedUntil: string | null;
  accessExpiresAt: string | null;
  suspensionReason: string | null;
  onUpdate: () => void;
}

type ActionType = 'suspend_temp' | 'deactivate' | 'set_access_expiry' | 'reactivate';

const ACTION_TO_API: Record<ActionType, string> = {
  suspend_temp: 'suspend_temp',
  deactivate: 'deactivate',
  set_access_expiry: 'set_access_expiry',
  reactivate: 'reactivate',
};

export function UserAccessManager({
  userId,
  userName,
  userEmail,
  isActive,
  suspendedUntil,
  accessExpiresAt,
  suspensionReason,
  onUpdate,
}: UserAccessManagerProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<ActionType | null>(null);
  const [suspendDays, setSuspendDays] = useState('7');
  const [accessDate, setAccessDate] = useState('');
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const now = new Date();
  const isSuspended = suspendedUntil && new Date(suspendedUntil) > now;
  const hasAccessExpiry = accessExpiresAt && new Date(accessExpiresAt) > now;
  const accessExpired = accessExpiresAt && new Date(accessExpiresAt) <= now;

  const getStatusBadge = () => {
    if (!isActive) {
      return (
        <Badge variant="destructive" className="gap-1">
          <UserX className="h-3 w-3" /> Desactivada
        </Badge>
      );
    }
    if (isSuspended) {
      return (
        <Badge variant="outline" className="gap-1 border-warning text-warning">
          <Clock className="h-3 w-3" /> Suspendida hasta{' '}
          {new Date(suspendedUntil!).toLocaleDateString('es-ES')}
        </Badge>
      );
    }
    if (accessExpired) {
      return (
        <Badge variant="outline" className="gap-1 border-destructive text-destructive">
          <AlertTriangle className="h-3 w-3" /> Acceso vencido
        </Badge>
      );
    }
    if (hasAccessExpiry) {
      return (
        <Badge variant="outline" className="gap-1 border-primary text-primary">
          <Calendar className="h-3 w-3" /> Acceso hasta{' '}
          {new Date(accessExpiresAt!).toLocaleDateString('es-ES')}
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="gap-1 border-success text-success">
        <CheckCircle className="h-3 w-3" /> Activa
      </Badge>
    );
  };

  const invokeAccess = async (action: ActionType) => {
    setIsLoading(true);
    try {
      const body: Record<string, unknown> = {
        userId,
        action: ACTION_TO_API[action],
      };
      if (action === 'suspend_temp') {
        body.suspendDays = parseInt(suspendDays, 10);
        if (reason.trim()) body.reason = reason.trim();
      }
      if (action === 'deactivate' && reason.trim()) body.reason = reason.trim();
      if (action === 'set_access_expiry') body.accessExpiresAt = accessDate;

      const { data, error } = await supabase.functions.invoke('admin-set-access', { body });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const messages: Record<ActionType, string> = {
        suspend_temp: 'Cuenta suspendida temporalmente',
        deactivate: 'Cuenta desactivada',
        set_access_expiry: 'Plazo de acceso configurado',
        reactivate: 'Cuenta reactivada',
      };
      toast.success(messages[action]);
      setDialogOpen(false);
      resetForm();
      onUpdate();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al actualizar acceso');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setActionType(null);
    setSuspendDays('7');
    setAccessDate('');
    setReason('');
  };

  const openWithAction = (action: ActionType) => {
    setActionType(action);
    setDialogOpen(true);
  };

  const canRestrict = isActive && !isSuspended;
  const canReactivate = isSuspended || !isActive || accessExpired;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Shield className="h-4 w-4 text-muted-foreground" />
        {getStatusBadge()}
      </div>

      {suspensionReason && (
        <p className="text-sm text-muted-foreground rounded-lg bg-muted/40 px-3 py-2">
          <span className="font-medium text-foreground">Motivo: </span>
          {suspensionReason}
        </p>
      )}

      <div className="grid gap-2 sm:grid-cols-2">
        {canRestrict && (
          <>
            <Button
              variant="outline"
              className="justify-start gap-2 h-auto py-3"
              onClick={() => openWithAction('suspend_temp')}
            >
              <Clock className="h-4 w-4 shrink-0" />
              <span className="text-left">
                <span className="block font-medium text-sm">Suspender temporalmente</span>
                <span className="block text-xs text-muted-foreground font-normal">
                  Bloqueo por días con motivo
                </span>
              </span>
            </Button>
            <Button
              variant="outline"
              className="justify-start gap-2 h-auto py-3 border-destructive/30 hover:bg-destructive/5"
              onClick={() => openWithAction('deactivate')}
            >
              <UserX className="h-4 w-4 shrink-0 text-destructive" />
              <span className="text-left">
                <span className="block font-medium text-sm">Desactivar cuenta</span>
                <span className="block text-xs text-muted-foreground font-normal">
                  Sin acceso hasta reactivación
                </span>
              </span>
            </Button>
            <Button
              variant="outline"
              className="justify-start gap-2 h-auto py-3 sm:col-span-2"
              onClick={() => openWithAction('set_access_expiry')}
            >
              <Calendar className="h-4 w-4 shrink-0" />
              <span className="text-left">
                <span className="block font-medium text-sm">Fijar fecha de fin de acceso</span>
                <span className="block text-xs text-muted-foreground font-normal">
                  Útil para mentorías con plazo (piloto B2B)
                </span>
              </span>
            </Button>
          </>
        )}

        {canReactivate && (
          <Button
            variant="outline"
            className="justify-start gap-2 h-auto py-3 border-success/30 hover:bg-success/5 sm:col-span-2"
            onClick={() => openWithAction('reactivate')}
          >
            <CheckCircle className="h-4 w-4 shrink-0 text-success" />
            <span className="text-left">
              <span className="block font-medium text-sm">Reactivar cuenta</span>
              <span className="block text-xs text-muted-foreground font-normal">
                Quita suspensión, desactivación y vencimiento
              </span>
            </span>
          </Button>
        )}

        {isSuspended && isActive && (
          <Button
            variant="outline"
            className="justify-start gap-2 h-auto py-3 border-destructive/30 sm:col-span-2"
            onClick={() => openWithAction('deactivate')}
          >
            <Ban className="h-4 w-4 shrink-0" />
            <span className="text-left text-sm">Convertir suspensión en desactivación</span>
          </Button>
        )}
      </div>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar cambio de acceso</DialogTitle>
            <DialogDescription>
              {userName} ({userEmail})
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {actionType === 'suspend_temp' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="days">Días de suspensión</Label>
                  <Input
                    id="days"
                    type="number"
                    min={1}
                    value={suspendDays}
                    onChange={(e) => setSuspendDays(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reason">Motivo (opcional)</Label>
                  <Textarea
                    id="reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                  />
                </div>
              </>
            )}

            {actionType === 'deactivate' && (
              <div className="space-y-2">
                <Label htmlFor="reason-deact">Motivo</Label>
                <Textarea
                  id="reason-deact"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Motivo de la desactivación…"
                  rows={3}
                />
                <p className="text-sm text-destructive">
                  El usuario verá un mensaje de bloqueo al intentar entrar.
                </p>
              </div>
            )}

            {actionType === 'set_access_expiry' && (
              <div className="space-y-2">
                <Label htmlFor="accessDate">Acceso válido hasta</Label>
                <Input
                  id="accessDate"
                  type="date"
                  value={accessDate}
                  onChange={(e) => setAccessDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            )}

            {actionType === 'reactivate' && (
              <p className="text-sm text-muted-foreground">
                Se restaurará el acceso completo y se eliminarán las restricciones actuales.
              </p>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            {actionType && (
              <Button
                onClick={() => invokeAccess(actionType)}
                disabled={isLoading}
                variant={actionType === 'deactivate' ? 'destructive' : 'default'}
              >
                {isLoading ? 'Procesando…' : 'Confirmar'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
